import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

interface PlanOption {
  id: string;
  name: string;
  slug: string;
  price_brl: number;
  periodicity: string;
  description: string | null;
  is_featured: boolean;
  accounts_limit: number;
  cards_limit: number;
  monthly_transactions_limit: number;
  goals_limit: number;
  reports_enabled: boolean;
}

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialPlanId?: string;
}

type Step = 'choose' | 'payment' | 'success' | 'pending' | 'rejected';

declare global {
  interface Window {
    MercadoPago: new (publicKey: string, options?: { locale?: string }) => {
      bricks: () => {
        create: (
          brick: string,
          containerId: string,
          options: Record<string, unknown>
        ) => Promise<{ unmount: () => void }>;
      };
    };
  }
}

function loadMpSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.MercadoPago) { resolve(); return; }
    const existing = document.getElementById('mp-sdk-script');
    if (existing) { existing.addEventListener('load', () => resolve()); return; }
    const script = document.createElement('script');
    script.id = 'mp-sdk-script';
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar SDK do Mercado Pago'));
    document.head.appendChild(script);
  });
}

const formatLimit = (v: number): string => (v === -1 ? 'Ilimitado' : String(v));

const formatPeriodicity = (p: string): string => {
  switch (p) {
    case 'monthly': return '/mês';
    case 'annual': return '/ano';
    case 'lifetime': return ' vitalício';
    default: return `/${p}`;
  }
};

const CheckoutModal = ({ open, onClose, onSuccess, initialPlanId }: CheckoutModalProps) => {
  const [step, setStep] = useState<Step>('choose');
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [brickLoading, setBrickLoading] = useState(true);
  const [activeGateway, setActiveGateway] = useState<{
    slug: string; public_key: string | null; environment: 'sandbox' | 'production'; test_payer_email?: string | null;
  } | null>(null);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const brickControllerRef = useRef<{ unmount: () => void } | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) {
      setStep('choose');
      setErrorMsg('');
      setFailureReason('');
      setBrickLoading(true);
      brickControllerRef.current?.unmount();
      brickControllerRef.current = null;
      return;
    }
    loadData();
  }, [open]);

  const loadData = async () => {
    setGatewayLoading(true);
    setPlansLoading(true);
    const [{ data: gw }, { data: session }, { data: plansData }] = await Promise.all([
      supabase.from('payment_gateways').select('slug, public_key, environment, test_payer_email').eq('is_active', true).eq('is_default', true).maybeSingle(),
      supabase.auth.getSession(),
      supabase.from('plans').select('id, name, slug, price_brl, periodicity, description, is_featured, accounts_limit, cards_limit, monthly_transactions_limit, goals_limit, reports_enabled').eq('is_active', true).eq('is_public', true).gt('price_brl', 0).order('display_order'),
    ]);
    if (!mountedRef.current) return;
    setActiveGateway(gw as typeof activeGateway);
    setUserEmail(session?.session?.user?.email || '');
    setGatewayLoading(false);

    const loaded = (plansData as PlanOption[]) || [];
    setPlans(loaded);
    setPlansLoading(false);

    // Pre-select plan
    if (loaded.length > 0) {
      if (initialPlanId) {
        const found = loaded.find(p => p.id === initialPlanId);
        setSelectedPlanId(found?.id || loaded.find(p => p.is_featured)?.id || loaded[0].id);
      } else {
        setSelectedPlanId(loaded.find(p => p.is_featured)?.id || loaded[0].id);
      }
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId) ?? null;

  const initBrick = useCallback(async () => {
    if (!activeGateway?.public_key || !selectedPlan) return;
    setBrickLoading(true);
    setErrorMsg('');

    brickControllerRef.current?.unmount();
    brickControllerRef.current = null;

    try {
      await loadMpSdk();
      if (!mountedRef.current) return;

      const mp = new window.MercadoPago(activeGateway.public_key, { locale: 'pt-BR' });
      const bricks = mp.bricks();

      const isSandbox = activeGateway.environment === 'sandbox';
      const brickPayerEmail = isSandbox && activeGateway.test_payer_email
        ? activeGateway.test_payer_email
        : userEmail;

      const planAmount = Number(selectedPlan.price_brl);
      const planId = selectedPlan.id;
      const billingCycle = selectedPlan.periodicity === 'annual' ? 'annual' : 'monthly';

      const controller = await bricks.create('cardPayment', 'mp-card-brick-container', {
        initialization: {
          amount: planAmount,
          payer: { email: brickPayerEmail },
        },
        customization: {
          paymentMethods: { maxInstallments: 1, minInstallments: 1 },
          visual: {
            style: {
              theme: 'default',
              customVariables: {
                baseColor: '#059669',
                baseColorFirstVariant: '#047857',
                baseColorSecondVariant: '#065f46',
                errorColor: '#ef4444',
                fontSizeSmall: '12px',
                fontSizeMedium: '14px',
                fontSizeLarge: '16px',
                borderRadiusFull: '8px',
                borderRadiusMedium: '8px',
                borderRadiusSmall: '6px',
                formBackgroundColor: '#f9fafb',
                inputBackgroundColor: '#ffffff',
                inputHoverColor: '#f0fdf4',
                inputFocusedColor: '#f0fdf4',
                inputErrorColor: '#fef2f2',
                labelColor: '#374151',
                inputTextColor: '#111827',
                buttonTextColor: '#ffffff',
                buttonBackground: '#059669',
                secondaryColor: '#6b7280',
              },
            },
            texts: {
              formTitle: 'Dados do cartão',
              cardholderName: { label: 'Nome no cartão', placeholder: 'Como aparece no cartão' },
              expirationDate: { label: 'Validade' },
              securityCode: { label: 'CVV' },
              installmentsSectionTitle: 'Parcelas',
              selectInstallments: 'Selecione',
              formSubmit: `Pagar R$ ${planAmount.toFixed(2).replace('.', ',')}`,
              emailSectionTitle: 'E-mail do pagador',
              email: { label: 'E-mail', placeholder: 'seu@email.com' },
              documentSectionTitle: 'Documento',
            },
          },
        },
        callbacks: {
          onReady: () => {
            if (mountedRef.current) setBrickLoading(false);
          },
          onSubmit: async (formData: Record<string, unknown>) => {
            await processPayment(formData, planId, planAmount, billingCycle);
          },
          onError: (error: { message?: string; type?: string }) => {
            console.error('[Brick error]', error);
            if (mountedRef.current && error.type !== 'non_critical') {
              setErrorMsg('Erro no formulário de pagamento. Verifique os dados e tente novamente.');
            }
          },
        },
      });

      if (mountedRef.current) {
        brickControllerRef.current = controller;
      } else {
        controller.unmount();
      }
    } catch (err: unknown) {
      console.error('[initBrick error]', err);
      if (mountedRef.current) {
        setErrorMsg('Não foi possível carregar o formulário de pagamento. Recarregue a página e tente novamente.');
        setBrickLoading(false);
      }
    }
  }, [activeGateway, selectedPlan, userEmail]);

  useEffect(() => {
    if (step === 'payment' && activeGateway && !gatewayLoading && selectedPlan) {
      const timer = setTimeout(() => { initBrick(); }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step, activeGateway, gatewayLoading, initBrick, selectedPlan]);

  useEffect(() => {
    return () => {
      brickControllerRef.current?.unmount();
      brickControllerRef.current = null;
    };
  }, []);

  const processPayment = async (
    formData: Record<string, unknown>,
    planId: string,
    amount: number,
    billingCycle: string,
  ) => {
    if (!mountedRef.current) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('Sessão expirada. Faça login novamente.');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/mp-process-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ plan_id: planId, amount, billing_cycle: billingCycle, formData }),
        }
      );

      const result = await response.json();
      if (!mountedRef.current) return;

      if (!result.success) {
        setErrorMsg(result.error || 'Erro ao processar pagamento. Tente novamente.');
        return;
      }

      brickControllerRef.current?.unmount();
      brickControllerRef.current = null;

      if (result.approved) {
        setStep('success');
        setTimeout(() => { if (mountedRef.current) onSuccess(); }, 3000);
      } else if (result.pending) {
        setStep('pending');
      } else if (result.rejected) {
        const reason = result.failure_reason || result.status_detail || '';
        setFailureReason(reason);
        setStep('rejected');
      } else {
        setErrorMsg('Resposta inesperada do servidor. Tente novamente.');
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Erro de conexão. Verifique sua internet.';
        setErrorMsg(msg);
      }
    }
  };

  const handleRetryPayment = () => {
    setBrickLoading(true);
    setErrorMsg('');
    setFailureReason('');
    setStep('payment');
  };

  const handleProceedToPayment = () => {
    setStep('payment');
    setErrorMsg('');
  };

  const handleBackToChoose = () => {
    brickControllerRef.current?.unmount();
    brickControllerRef.current = null;
    setBrickLoading(true);
    setStep('choose');
    setErrorMsg('');
  };

  if (!open) return null;

  const isSandbox = activeGateway?.environment === 'sandbox';
  const hasGateway = !!activeGateway;
  const missingTestPayerEmail = isSandbox && !activeGateway?.test_payer_email;
  const cheapestPlan = plans.length > 0 ? plans.reduce((a, b) => Number(a.price_brl) < Number(b.price_brl) ? a : b) : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={step === 'choose' ? onClose : undefined}
      />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <i className={`text-lg ${step === 'success' ? 'ri-checkbox-circle-fill' : step === 'pending' ? 'ri-time-line' : step === 'rejected' ? 'ri-close-circle-fill' : 'ri-vip-crown-fill'}`} />
              </div>
              <div>
                <p className="text-sm font-bold">
                  {step === 'choose' && 'Escolha seu plano'}
                  {step === 'payment' && 'Dados do pagamento'}
                  {step === 'success' && 'Pagamento aprovado!'}
                  {step === 'pending' && 'Pagamento em análise'}
                  {step === 'rejected' && 'Pagamento recusado'}
                </p>
                <p className="text-emerald-100 text-xs">
                  {step === 'choose' && 'Selecione o plano ideal para você'}
                  {step === 'payment' && selectedPlan && `${selectedPlan.name} · R$ ${Number(selectedPlan.price_brl).toFixed(2).replace('.', ',')}${formatPeriodicity(selectedPlan.periodicity)}`}
                  {step === 'success' && 'Seu plano foi ativado'}
                  {step === 'pending' && 'Você receberá confirmação por e-mail'}
                  {step === 'rejected' && 'O plano não foi ativado'}
                </p>
              </div>
            </div>
            {(step === 'choose' || step === 'success' || step === 'pending' || step === 'rejected') && (
              <button
                onClick={step === 'choose' ? onClose : step === 'rejected' ? onClose : onSuccess}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>

          {(step === 'choose' || step === 'payment') && (
            <div className="flex items-center gap-2 mt-4">
              {['Plano', 'Pagamento'].map((label, i) => (
                <div key={label} className="flex items-center gap-2">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      (step === 'choose' && i === 0) || (step === 'payment' && i === 1)
                        ? 'bg-white text-emerald-700'
                        : step === 'payment' && i === 0
                        ? 'bg-white/40 text-white'
                        : 'bg-white/20 text-white'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className="text-xs text-emerald-100">{label}</span>
                  {i === 0 && <i className="ri-arrow-right-s-line text-emerald-200 text-sm" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {/* STEP 1: Choose plan */}
          {step === 'choose' && (
            <div className="space-y-4">
              {plansLoading ? (
                <div className="space-y-3">
                  {[1, 2].map(i => (
                    <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : plans.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <i className="ri-error-warning-line text-amber-500 text-2xl mb-2" />
                  <p className="text-sm text-amber-700 font-semibold">Nenhum plano disponível</p>
                  <p className="text-xs text-amber-600 mt-1">Entre em contato com o suporte.</p>
                </div>
              ) : (
                plans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedPlanId === plan.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            selectedPlanId === plan.id ? 'border-emerald-500' : 'border-gray-300'
                          }`}
                        >
                          {selectedPlanId === plan.id && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-gray-900">{plan.name}</p>
                            {plan.is_featured && (
                              <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-500 text-white rounded-full">
                                Mais popular
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <i className="ri-bank-line text-gray-400" />
                              {formatLimit(plan.accounts_limit)} contas
                            </span>
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <i className="ri-bank-card-line text-gray-400" />
                              {formatLimit(plan.cards_limit)} cartões
                            </span>
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <i className="ri-swap-line text-gray-400" />
                              {formatLimit(plan.monthly_transactions_limit)} lançamentos/mês
                            </span>
                            {plan.reports_enabled && (
                              <span className="text-[11px] text-emerald-600 flex items-center gap-1">
                                <i className="ri-bar-chart-2-line" />
                                Relatórios avançados
                              </span>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-gray-400 mt-0.5">{plan.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-bold text-gray-900">
                          R$ {Number(plan.price_brl).toFixed(2).replace('.', ',')}
                        </p>
                        <p className="text-xs text-gray-400">{formatPeriodicity(plan.periodicity)}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}

              {!gatewayLoading && (
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                  {hasGateway ? (
                    <>
                      <i className="ri-shield-check-line text-emerald-500 text-base flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-gray-700">
                          Pagamento seguro via Mercado Pago
                          {isSandbox && (
                            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                              SANDBOX
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">Cartão de crédito · Seus dados são protegidos</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <i className="ri-error-warning-line text-amber-500 text-base flex-shrink-0" />
                      <p className="text-xs text-amber-600">
                        Nenhum gateway de pagamento configurado. Configure o Mercado Pago no painel administrativo.
                      </p>
                    </>
                  )}
                </div>
              )}

              {!gatewayLoading && missingTestPayerEmail && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-amber-500 text-sm flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    <strong>Config. incompleta:</strong> Configure o e-mail do comprador de teste no painel Admin → Gateways para que o sandbox funcione corretamente.
                  </p>
                </div>
              )}

              <button
                onClick={handleProceedToPayment}
                disabled={gatewayLoading || plansLoading || !hasGateway || !selectedPlanId}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {gatewayLoading || plansLoading ? (
                  <><i className="ri-loader-4-line animate-spin" />Carregando...</>
                ) : cheapestPlan ? (
                  <>Continuar para pagamento<i className="ri-arrow-right-line" /></>
                ) : (
                  'Continuar para pagamento'
                )}
              </button>
            </div>
          )}

          {/* STEP 2: Payment Brick */}
          {step === 'payment' && selectedPlan && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-900">{selectedPlan.name}</p>
                  <p className="text-xs text-gray-500">{selectedPlan.description || `Plano ${selectedPlan.name}`}</p>
                </div>
                <p className="text-base font-bold text-emerald-600 whitespace-nowrap ml-3">
                  R$ {Number(selectedPlan.price_brl).toFixed(2).replace('.', ',')}
                </p>
              </div>

              {isSandbox && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-flask-line text-yellow-500 text-sm flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-yellow-700">Ambiente de teste (sandbox)</p>
                    <p className="text-xs text-yellow-600 mt-0.5">
                      Cartão de teste: <strong>4509 9535 6623 3704</strong> · CVV: <strong>123</strong> · Validade: qualquer data futura · Nome: <strong>APRO</strong> · CPF: <strong>12345678909</strong>
                    </p>
                    {activeGateway?.test_payer_email && (
                      <p className="text-xs text-yellow-600 mt-1">
                        E-mail do pagador (teste): <strong>{activeGateway.test_payer_email}</strong>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {brickLoading && (
                <div className="space-y-2.5 animate-pulse">
                  <div className="h-12 bg-gray-100 rounded-lg" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-12 bg-gray-100 rounded-lg" />
                    <div className="h-12 bg-gray-100 rounded-lg" />
                  </div>
                  <div className="h-12 bg-gray-100 rounded-lg" />
                  <div className="h-12 bg-gray-100 rounded-lg" />
                  <div className="h-10 bg-emerald-100 rounded-lg" />
                  <p className="text-center text-xs text-gray-400">Carregando formulário seguro...</p>
                </div>
              )}

              <div id="mp-card-brick-container" className={brickLoading ? 'hidden' : ''} />

              {errorMsg && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600">{errorMsg}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleBackToChoose}
                className="w-full py-2.5 border border-gray-200 text-gray-500 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                <i className="ri-arrow-left-line mr-1.5" />
                Voltar
              </button>

              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                <i className="ri-lock-line" />
                Pagamento processado com segurança pelo Mercado Pago
              </p>
            </div>
          )}

          {/* STEP: Rejected */}
          {step === 'rejected' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-close-circle-fill text-3xl text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pagamento recusado</h3>
              <p className="text-sm text-gray-500 mb-1">Seu plano não foi ativado.</p>

              {failureReason ? (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-left mt-3 mb-5 flex items-start gap-2">
                  <i className="ri-information-line text-red-400 text-sm flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-red-700 mb-0.5">Motivo da recusa</p>
                    <p className="text-xs text-red-600">{failureReason}</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 mb-5">Verifique os dados do cartão ou tente com outro cartão.</p>
              )}

              <div className="space-y-2 mt-4">
                <button
                  onClick={handleRetryPayment}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
                >
                  <i className="ri-refresh-line" />
                  Tentar novamente
                </button>
                <button
                  onClick={handleRetryPayment}
                  className="w-full py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-bank-card-line" />
                  Usar outro cartão
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-2.5 text-gray-400 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap hover:text-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* STEP: Success */}
          {step === 'success' && selectedPlan && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-3xl text-emerald-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Plano ativado!</h3>
              <p className="text-sm text-gray-500 mb-1">Seu pagamento foi aprovado com sucesso.</p>
              <p className="text-xs text-gray-400 mb-5">Plano <strong>{selectedPlan.name}</strong> ativo agora.</p>
              <div className="bg-emerald-50 rounded-xl p-3 text-left space-y-2 mb-5">
                {[
                  `${formatLimit(selectedPlan.accounts_limit)} contas bancárias`,
                  `${formatLimit(selectedPlan.cards_limit)} cartões de crédito`,
                  `${formatLimit(selectedPlan.monthly_transactions_limit)} lançamentos/mês`,
                  selectedPlan.reports_enabled ? 'Relatórios avançados desbloqueados' : null,
                ].filter(Boolean).map((f) => (
                  <div key={f!} className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0" />
                    <span className="text-xs text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">Fechando automaticamente...</p>
            </div>
          )}

          {/* STEP: Pending */}
          {step === 'pending' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-yellow-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pagamento em análise</h3>
              <p className="text-sm text-gray-500 mb-1">Seu pagamento está sendo processado.</p>
              <p className="text-xs text-gray-400 mb-5">
                Você receberá uma confirmação por e-mail assim que for aprovado. Seu plano será ativado automaticamente.
              </p>
              <button
                onClick={onSuccess}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                Entendido, fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
export { CheckoutModal };
